import { convex } from "@/lib/convexClient";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";

type Bucket = "staging" | "exports";

const toError = (message: string) => ({ message });

function getBunnyConfig() {
  const storageZone = import.meta.env.VITE_BUNNY_STORAGE_ZONE as string | undefined;
  const accessKey = import.meta.env.VITE_BUNNY_ACCESS_KEY as string | undefined;
  const storageHost =
    (import.meta.env.VITE_BUNNY_STORAGE_HOST as string | undefined) || "storage.bunnycdn.com";
  const rawPullZoneHost =
    (import.meta.env.VITE_BUNNY_PULL_ZONE_HOST as string | undefined) ||
    (storageZone ? `${storageZone}.b-cdn.net` : undefined);
  // Normalize: strip protocol and trailing slashes
  const pullZoneHost = rawPullZoneHost
    ? rawPullZoneHost.replace(/^https?:\/\//, "").replace(/\/+$/, "")
    : undefined;
  return { storageZone, accessKey, storageHost, pullZoneHost };
}

export function isBunnyConfigured(): boolean {
  const { storageZone, accessKey } = getBunnyConfig();
  return !!(storageZone && accessKey);
}

export function getBunnyPullZoneHost(): string | undefined {
  return getBunnyConfig().pullZoneHost;
}

// Bucket parameter is kept in all method signatures for interface compatibility
// but is NOT used in URL construction — paths start directly with the workspace ID

function storageUrl(storageHost: string, storageZone: string, path: string) {
  return `https://${storageHost}/${storageZone}/${path.replace(/^\//, "")}`;
}

function cdnUrl(pullZoneHost: string, path: string) {
  return `https://${pullZoneHost}/${path.replace(/^\//, "")}`;
}

export const BunnyStorageClient = {
  async upload(
    _bucket: Bucket,
    path: string,
    file: File | Blob,
    _options?: { cacheControl?: string; upsert?: boolean },
  ) {
    try {
      const { storageZone, accessKey, storageHost } = getBunnyConfig();
      if (!storageZone || !accessKey) {
        return { data: null, error: toError("Bunny storage is not configured") };
      }

      const url = storageUrl(storageHost, storageZone, path);
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          AccessKey: accessKey,
          "Content-Type": (file as File).type || "application/octet-stream",
        },
        body: file,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
      }

      return {
        data: { path, fullPath: path, id: path },
        error: null,
      };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Upload failed";
      return { data: null, error: toError(message) };
    }
  },

  async createSignedUrl(
    _bucket: Bucket,
    path: string,
    expiresIn: number,
    craftspaceId?: string,
  ) {
    try {
      const { pullZoneHost } = getBunnyConfig();

      // If no craftspaceId, fall back to plain CDN URL (no token auth)
      if (!craftspaceId) {
        const signedUrl = pullZoneHost
          ? cdnUrl(pullZoneHost, path)
          : path;
        return { data: { signedUrl }, error: null };
      }

      const signedUrl = await convex.action(api.actions.bunnyTokenAuth.generateTokenUrl, {
        path,
        craftspaceId: craftspaceId as Id<"craftspaces">,
        expiresIn,
      });
      return { data: { signedUrl }, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to create signed URL";
      return { data: null, error: toError(message) };
    }
  },

  async getPublicUrl(_bucket: Bucket, path: string) {
    const { pullZoneHost } = getBunnyConfig();
    if (!pullZoneHost) return { data: { publicUrl: "" } };
    return { data: { publicUrl: cdnUrl(pullZoneHost, path) } };
  },

  async remove(_bucket: Bucket, paths: string[]) {
    try {
      const { storageZone, accessKey, storageHost } = getBunnyConfig();
      if (!storageZone || !accessKey) {
        return { data: null, error: toError("Bunny storage is not configured") };
      }

      await Promise.all(
        paths.map((path) =>
          fetch(storageUrl(storageHost, storageZone, path), {
            method: "DELETE",
            headers: { AccessKey: accessKey },
          }),
        ),
      );

      return { data: null, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to remove files";
      return { data: null, error: toError(message) };
    }
  },

  // Delete an entire directory and all its contents in one API call.
  // Bunny Storage deletes a folder when the path ends with "/".
  async removeFolder(path: string) {
    try {
      const { storageZone, accessKey, storageHost } = getBunnyConfig();
      if (!storageZone || !accessKey) {
        return { error: toError("Bunny storage is not configured") };
      }

      const cleanPath = path.replace(/^\//, "").replace(/\/?$/, "/");
      const url = `https://${storageHost}/${storageZone}/${cleanPath}`;
      const response = await fetch(url, {
        method: "DELETE",
        headers: { AccessKey: accessKey },
      });

      if (!response.ok && response.status !== 404) {
        throw new Error(`Delete folder failed: ${response.status} ${response.statusText}`);
      }

      return { error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to delete folder";
      return { error: toError(message) };
    }
  },

  async list(_bucket: Bucket, path: string) {
    try {
      const { storageZone, accessKey, storageHost } = getBunnyConfig();
      if (!storageZone || !accessKey) {
        return { data: null, error: toError("Bunny storage is not configured") };
      }

      const cleanPath = path.replace(/^\//, "").replace(/\/?$/, "/");
      const url = `https://${storageHost}/${storageZone}/${cleanPath}`;
      const response = await fetch(url, {
        headers: { AccessKey: accessKey, accept: "application/json" },
      });

      if (!response.ok) {
        throw new Error(`List failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return { data, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to list files";
      return { data: null, error: toError(message) };
    }
  },

  async download(_bucket: Bucket, path: string) {
    try {
      const { pullZoneHost } = getBunnyConfig();
      if (!pullZoneHost) {
        return { data: null, error: toError("Bunny storage is not configured") };
      }

      const response = await fetch(cdnUrl(pullZoneHost, path));
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.blob();
      return { data, error: null };
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Failed to download file";
      return { data: null, error: toError(message) };
    }
  },

  async createSignedUploadUrl(_path: string) {
    return {
      data: null,
      error: toError(
        "Direct signed upload URLs are not supported with Bunny Storage; use upload() instead",
      ),
    };
  },
};
